import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Sun, Moon, BookOpen, Home, Sparkles, Menu, X } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

interface LayoutProps {
  children: ReactNode
  isDark: boolean
  toggleTheme: () => void
}

export default function Layout({ children, isDark, toggleTheme }: LayoutProps) {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/demo', label: 'Sacred Journey', icon: Sparkles }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-divine-50 transition-colors duration-500">
      {/* Sacred background pattern */}
      <div className="fixed inset-0 bg-sacred-pattern opacity-50 pointer-events-none" />
      <div className="fixed inset-0 bg-divine-rays opacity-30 pointer-events-none animate-slow-spin" style={{ animationDuration: '120s' }} />

      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <nav className="bg-white/80 dark:bg-divine-50/80 backdrop-blur-xl border-b border-gold-200/50 dark:border-gold-800/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 group">
                <motion.div
                  whileHover={{ rotate: 5, scale: 1.05 }}
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-sacred"
                >
                  <BookOpen className="w-5 h-5 text-white" />
                </motion.div>
                <div className="hidden sm:block">
                  <p className="font-display text-xl font-bold text-gold-gradient">
                    Godly Library
                  </p>
                  <p className="text-xs text-gold-600/70 dark:text-gold-400/70 -mt-1">
                    Divine Wisdom Archive
                  </p>
                </div>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-8">
                {navLinks.map((link) => {
                  const Icon = link.icon
                  const isActive = location.pathname === link.path
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={cn(
                        'nav-link flex items-center gap-2',
                        isActive && 'text-gold-500'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {link.label}
                    </Link>
                  )
                })}
              </div>

              {/* Right side actions */}
              <div className="flex items-center gap-4">
                {/* Theme Toggle */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleTheme}
                  className="p-2 rounded-xl bg-gold-100 dark:bg-gold-900/30 text-gold-600 dark:text-gold-400 hover:bg-gold-200 dark:hover:bg-gold-800/40 transition-colors"
                  aria-label="Toggle theme"
                >
                  <AnimatePresence mode="wait">
                    {isDark ? (
                      <motion.div
                        key="sun"
                        initial={{ rotate: -90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: 90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Sun className="w-5 h-5" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="moon"
                        initial={{ rotate: 90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: -90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Moon className="w-5 h-5" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>

                {/* Mobile menu button */}
                <button
                  className="md:hidden p-2 rounded-xl bg-gold-100 dark:bg-gold-900/30 text-gold-600 dark:text-gold-400"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden bg-white/95 dark:bg-divine-50/95 border-t border-gold-200/50 dark:border-gold-800/30"
              >
                <div className="px-4 py-4 space-y-2">
                  {navLinks.map((link) => {
                    const Icon = link.icon
                    const isActive = location.pathname === link.path
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 rounded-xl transition-colors',
                          isActive
                            ? 'bg-gold-100 dark:bg-gold-900/30 text-gold-600 dark:text-gold-400'
                            : 'text-gold-700 dark:text-gold-300 hover:bg-gold-50 dark:hover:bg-gold-900/20'
                        )}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{link.label}</span>
                      </Link>
                    )
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </header>

      {/* Main Content */}
      <main className="pt-16 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-10 mt-20 border-t border-gold-200/50 dark:border-gold-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-gold-gradient">
                    Godly Library
                  </h3>
                </div>
              </div>
              <p className="text-sm text-gold-700/70 dark:text-gold-300/70">
                A reverent archive of sacred wisdom, honoring the divine teachings that have guided humanity across millennia.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-display font-semibold text-gold-800 dark:text-gold-200 mb-4">
                Sacred Paths
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/demo" className="text-gold-600/70 dark:text-gold-400/70 hover:text-gold-500 transition-colors">
                    Begin Your Journey
                  </Link>
                </li>
                <li>
                  <span className="text-gold-600/70 dark:text-gold-400/70">
                    World Faiths
                  </span>
                </li>
                <li>
                  <span className="text-gold-600/70 dark:text-gold-400/70">
                    Sacred Texts
                  </span>
                </li>
              </ul>
            </div>

            {/* Sacred Quote */}
            <div>
              <h4 className="font-display font-semibold text-gold-800 dark:text-gold-200 mb-4">
                Daily Wisdom
              </h4>
              <blockquote className="text-sm italic text-gold-700/70 dark:text-gold-300/70 border-l-2 border-gold-400 pl-4">
                "The light of divine wisdom illuminates all paths to truth."
              </blockquote>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-12 pt-6 border-t border-gold-200/30 dark:border-gold-800/20">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <div className="flex flex-col gap-2">
                <p className="text-sm text-gold-600/70 dark:text-gold-400/70">
                  © {new Date().getFullYear()} Godly Library. All rights reserved.
                </p>
                <a
                  href="https://globalknowledgegraph.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gold-600 dark:text-gold-400 hover:text-gold-500 transition-colors"
                >
                  Part of the Global Knowledge Graph Network
                </a>
                <a
                  href="mailto:contact@godlylibrary.com"
                  className="text-sm text-gold-600/70 dark:text-gold-400/70 hover:text-gold-500 transition-colors"
                >
                  contact@godlylibrary.com
                </a>
              </div>
              <div className="flex items-center gap-4 text-2xl">
                <span title="Christianity">✝</span>
                <span title="Islam">☪</span>
                <span title="Judaism">✡</span>
                <span title="Hinduism">🕉</span>
                <span title="Buddhism">☸</span>
                <span title="Sikhism">☬</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
